/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import theme from '../../../../../themes/theme';
import { InvoiceSortInput, Direction } from '../../../../../__generated__/pge-types';

type SortProps = {
  content: PageTextContentType;
  sort: (sortBy: InvoiceSortInput) => void;
  setSortByVal?: Dispatch<SetStateAction<string | undefined>>;
  value?: InvoiceSortInput;
  setOpenDrawer?: Dispatch<SetStateAction<boolean>>;
};

export const Sort = (props: SortProps) => {
  const { content, sort, setSortByVal } = props;
  const [val, setVal] = useState<InvoiceSortInput | undefined>(props?.value);
  const [sortDirection, setSortDirection] = useState<Direction>(
    Direction.Asc,
  );

  useEffect(() => {
    //Make the sort and direction controlled from the values passed.
    if (props.value) {
      const key = Object.keys(props.value)?.[0];
      if (key) {
        setVal(props.value);
        setSortDirection(Object.values(props.value)?.[0] as Direction);
      }
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVal({ [e.target.value]: sortDirection });
    e.target.value === 'dueDate'
      ? setSortByVal && setSortByVal(content.get('DUE_DATE'))
      : e.target.value === 'billAmount'
      ? setSortByVal && setSortByVal(content.get('BILL_AMOUNT'))
      : undefined;
    sort && sort({ [e.target.value]: sortDirection });
    props.setOpenDrawer && props.setOpenDrawer(false);
  };
  const handleSort = (direction: Direction) => {
    if (!Boolean(val)) {
      setVal({ dueDate: direction });
      sort({ dueDate: direction });
    }
    setSortDirection(direction);
    val && sort && sort({ ...val, [Object.keys(val)[0]]: direction });
    props.setOpenDrawer && props.setOpenDrawer(false);
  };

  return (
    <Fragment>
      <Box p={1} pl={1.5} pb={0}>
        <FormControl component="fieldset">
          <RadioGroup
            name="sort-by"
            onChange={handleChange}
            value={val ? Object.keys(val)?.[0] : null}
          >
            <FormControlLabel
              value="dueDate"
              control={<Radio size="small" color="primary" />}
              label={
                <Typography
                  css={{
                    fontSize: theme.spacing(2),
                  }}
                >
                  {content.get('DUE_DATE')}
                </Typography>
              }
            />
            <FormControlLabel
              value="billAmount"
              control={<Radio size="small" color="primary" />}
              label={
                <Typography
                  css={{
                    fontSize: theme.spacing(2),
                  }}
                >
                  {`${content.get('BILL_AMOUNT')}.`}
                </Typography>
              }
            />           
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider
        css={{
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(2),
        }}
      />
      <Box p={1} pt={0} pb={2}>
        {(val?.hasOwnProperty('dueDate') || val === undefined) && (
          <Fragment>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <ArrowDownwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  ...(sortDirection === Direction.Desc && {
                    fontWeight: 'bold',
                  }),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                }}
                onClick={() => handleSort(Direction.Desc)}
              >
                {content.get('NEWEST_TO_OLDEST')}
              </Typography>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                  ...(sortDirection === Direction.Asc && {
                    fontWeight: 'bold',
                  }),
                }}
                onClick={() => handleSort(Direction.Asc)}
              >
                {content.get('OLDEST_TO_NEWEST')}
              </Typography>
            </Box>
          </Fragment>
        )}
        {val?.hasOwnProperty('billAmount') && (
          <Fragment>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <ArrowDownwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  ...(sortDirection === Direction.Asc && {
                    fontWeight: 'bold',
                  }),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                }}
                onClick={() => handleSort(Direction.Asc)}
              >
                {content.get('ASC')}
              </Typography>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                  ...(sortDirection === Direction.Desc && {
                    fontWeight: 'bold',
                  }),
                }}
                onClick={() => handleSort(Direction.Desc)}
              >
                {content.get('DESC')}
              </Typography>
            </Box>
          </Fragment>
        )}        
      </Box>
    </Fragment>
  );
};
